import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  DollarSign, 
  ShoppingBag, 
  Users, 
  Package, 
  ArrowUp, 
  ArrowDown,
  Clock,
  CheckCircle,
  Truck,
  XCircle,
  AlertCircle,
  BarChart3,
  PieChart
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function AnalyticsDashboard() {
  const [overview, setOverview] = useState(null);
  const [salesChart, setSalesChart] = useState({ labels: [], revenue: [], orders: [] });
  const [topProducts, setTopProducts] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [chartPeriod, setChartPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const [overviewRes, topProductsRes, recentOrdersRes, chartRes] = await Promise.all([
        api.get('/analytics/overview'),
        api.get('/analytics/top-products?limit=5'),
        api.get('/analytics/recent-orders?limit=5'),
        api.get(`/analytics/sales-chart?period=${chartPeriod}`)
      ]);
      setOverview(overviewRes.data);
      setTopProducts(topProductsRes.data);
      setRecentOrders(recentOrdersRes.data);
      setSalesChart(chartRes.data);
      setInitialLoadDone(true);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [chartPeriod]);

  const fetchSalesChart = useCallback(async () => {
    try {
      const res = await api.get(`/analytics/sales-chart?period=${chartPeriod}`);
      setSalesChart(res.data);
    } catch (error) {
      // Chart fetch failed silently
    }
  }, [chartPeriod]);

  useEffect(() => {
    // Wait for auth to complete loading
    if (authLoading) return;
    
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchAnalytics();
  }, [user, authLoading, navigate, fetchAnalytics]);

  useEffect(() => {
    // Only fetch chart data after initial load is done and user is authenticated
    if (initialLoadDone && user?.role === 'admin') {
      fetchSalesChart();
    }
  }, [chartPeriod, initialLoadDone, user, fetchSalesChart]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getMaxValue = (arr) => Math.max(...arr, 1);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a3a5c]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="analytics-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-bold text-[#1a3a5c]">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">Monitor your store performance and sales metrics</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Revenue */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            data-testid="stat-revenue"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Revenue</p>
                <p className="text-2xl font-bold text-[#1a3a5c]">{formatCurrency(overview?.revenue?.total || 0)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Today: {formatCurrency(overview?.revenue?.today || 0)}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          {/* Total Orders */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            data-testid="stat-orders"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Orders</p>
                <p className="text-2xl font-bold text-[#1a3a5c]">{overview?.orders?.total || 0}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Completed: {overview?.orders?.completed || 0}
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <ShoppingBag className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          {/* Total Customers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            data-testid="stat-customers"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Customers</p>
                <p className="text-2xl font-bold text-[#1a3a5c]">{overview?.customers?.total || 0}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Registered users
                </p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </motion.div>

          {/* Avg Order Value */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            data-testid="stat-aov"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Avg Order Value</p>
                <p className="text-2xl font-bold text-[#1a3a5c]">{formatCurrency(overview?.average_order_value || 0)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Per completed order
                </p>
              </div>
              <div className="bg-orange-100 p-3 rounded-full">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Sales Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            data-testid="sales-chart"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-lg font-semibold text-[#1a3a5c] flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Sales Overview
              </h2>
              <div className="flex space-x-2">
                {['week', 'month', 'year'].map((period) => (
                  <button
                    key={period}
                    onClick={() => setChartPeriod(period)}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      chartPeriod === period
                        ? 'bg-[#1a3a5c] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`period-${period}`}
                  >
                    {period.charAt(0).toUpperCase() + period.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Simple Bar Chart */}
            <div className="h-64 flex items-end justify-between space-x-1 px-2">
              {salesChart.labels.map((label, index) => {
                const maxRevenue = getMaxValue(salesChart.revenue);
                const height = (salesChart.revenue[index] / maxRevenue) * 100;
                return (
                  <div key={`chart-${label}`} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-gradient-to-t from-[#1a3a5c] to-[#059669] rounded-t transition-all duration-500 hover:opacity-80 relative group"
                      style={{ height: `${Math.max(height, 2)}%` }}
                    >
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                        {formatCurrency(salesChart.revenue[index])}
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 mt-2 truncate w-full text-center">{label}</span>
                  </div>
                );
              })}
            </div>
            
            <div className="flex items-center justify-center space-x-6 mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-[#1a3a5c] rounded mr-2"></div>
                <span className="text-sm text-gray-600">Revenue</span>
              </div>
              <div className="text-sm text-gray-500">
                Total: {formatCurrency(salesChart.revenue.reduce((a, b) => a + b, 0))}
              </div>
            </div>
          </motion.div>

          {/* Order Status Breakdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            data-testid="order-status"
          >
            <h2 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-6 flex items-center">
              <PieChart className="h-5 w-5 mr-2" />
              Order Status
            </h2>
            
            <div className="space-y-3">
              {[
                { status: 'pending', label: 'Pending', icon: Clock, color: 'bg-gray-100 text-gray-600' },
                { status: 'confirmed', label: 'Confirmed', icon: CheckCircle, color: 'bg-blue-100 text-blue-600' },
                { status: 'processing', label: 'Processing', icon: Package, color: 'bg-purple-100 text-purple-600' },
                { status: 'shipped', label: 'Shipped', icon: Truck, color: 'bg-orange-100 text-orange-600' },
                { status: 'delivered', label: 'Delivered', icon: CheckCircle, color: 'bg-green-100 text-green-600' },
                { status: 'cancelled', label: 'Cancelled', icon: XCircle, color: 'bg-red-100 text-red-600' }
              ].map(({ status, label, icon: Icon, color }) => {
                const count = overview?.orders?.status_breakdown?.[status] || 0;
                const total = overview?.orders?.total || 1;
                const percentage = ((count / total) * 100).toFixed(0);
                
                return (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`p-1.5 rounded ${color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className="ml-2 text-sm text-gray-700">{label}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900 mr-2">{count}</span>
                      <span className="text-xs text-gray-500">({percentage}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {overview?.cancellations?.pending > 0 && (
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center text-yellow-800">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  <span className="text-sm font-medium">{overview.cancellations.pending} pending cancellation(s)</span>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Products */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            data-testid="top-products"
          >
            <h2 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-6 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2" />
              Top Selling Products
            </h2>
            
            {topProducts.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No sales data yet</p>
            ) : (
              <div className="space-y-4">
                {topProducts.map((product, index) => (
                  <div key={product.product_id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-gray-100 text-gray-600' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">{product.product_name}</p>
                        <p className="text-xs text-gray-500">{product.quantity_sold} sold</p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-[#1a3a5c]">{formatCurrency(product.revenue)}</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Recent Orders */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            data-testid="recent-orders"
          >
            <h2 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-6 flex items-center">
              <ShoppingBag className="h-5 w-5 mr-2" />
              Recent Orders
            </h2>
            
            {recentOrders.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No orders yet</p>
            ) : (
              <div className="space-y-3">
                {recentOrders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">#{order.tracking_id}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(order.created_at).toLocaleDateString('en-IN', { 
                          day: 'numeric', 
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#1a3a5c]">{formatCurrency(order.total_amount)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        order.payment_status === 'completed' ? 'bg-green-100 text-green-700' :
                        order.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {order.payment_status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* Products Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          data-testid="product-stats"
        >
          <h2 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-4">Product Inventory</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-700">{overview?.products?.active || 0}</p>
              <p className="text-sm text-green-600">Active Products</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-700">{overview?.products?.sold_out || 0}</p>
              <p className="text-sm text-red-600">Sold Out</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-700">{overview?.products?.total || 0}</p>
              <p className="text-sm text-blue-600">Total Products</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
